import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { listSentence } from '@/lib/formatters'
import {
  fillApplicationErrorMessage,
  IntakeMode,
  type IntakeRow,
  invalidateFillApplication,
  stopIntake,
} from '@/models/fillApplication'

interface StopReadingButtonProps {
  orgId: string
  /** Every row still being read, from any batch. */
  rows: IntakeRow[]
}

export function StopReadingButton({ orgId, rows }: StopReadingButtonProps) {
  const queryClient = useQueryClient()
  const customerFiles = rows
    .filter((row) => row.mode === IntakeMode.CUSTOMER_UPLOAD)
    .map((row) => row.fileName)
  const description = [
    `Cai stops reading ${pluralize('file', rows.length, true)}. Anything already filled in stays.`,
    customerFiles.length > 0
      ? `This includes the customer's own uploads: ${listSentence(customerFiles)}. Their chat will tell them Cai couldn't read those files.`
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  async function stop() {
    try {
      await stopIntake(orgId)
      await invalidateFillApplication(queryClient, orgId)
    } catch (error) {
      toast.error(fillApplicationErrorMessage(error))
    }
  }

  return (
    <ConfirmationDialog
      title="Stop reading these files?"
      description={description}
      confirmLabel="Stop reading"
      onConfirm={stop}
    >
      <Button type="button" variant="outline">
        Stop reading these files
      </Button>
    </ConfirmationDialog>
  )
}
